import { formatWhen } from "@/components/boo/access-copy";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, type ConnectorActivity, type ConnectorActivityKind, type ConnectorDetail, type ConnectorSourceKind } from "@/lib/api";
import { actorName, callOutcome, connectorKeys, type OutcomeTone } from "@/lib/connectors";
import { cn } from "@/lib/utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CheckCircle2, Code2, KeyRound, Play, Plug, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Segmented } from "./parts";

const KIND_ICON: Record<ConnectorActivityKind, typeof Play> = {
  call: Play,
  code: Code2,
  variable: KeyRound,
  setting: SlidersHorizontal,
  approval: CheckCircle2,
  connection: Plug,
};

const TONE_CLASS: Record<OutcomeTone, string> = {
  ok: "text-emerald-700 dark:text-emerald-400",
  ask: "text-amber-700 dark:text-amber-400",
  off: "text-muted-foreground",
  bad: "text-destructive",
};

const KIND_LABEL: Record<Exclude<ConnectorActivityKind, "call">, string> = {
  code: "Code",
  variable: "Values",
  setting: "Settings",
  approval: "Approval",
  connection: "Connection",
};

type Filter = "all" | "calls" | "changes";

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Everything" },
  { value: "calls", label: "Calls" },
  { value: "changes", label: "Changes" },
];

// Every call Boo made through the connector and every change to its code,
// values and settings, newest first. Values themselves are never listed.
export function ActivityPanel({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const [filter, setFilter] = useState<Filter>("all");
  const query = useInfiniteQuery({
    queryKey: connectorKeys.activity(containerId, connector.slug),
    queryFn: ({ pageParam }) => api.connectors.activity(containerId, connector.slug, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError) return <InlineError error={query.error} />;
  const items = (query.data?.pages ?? []).flatMap((p) => p.items);
  const shown = items.filter((e) => filter === "all" || (filter === "calls") === (e.kind === "call"));

  return (
    <div className="flex flex-col gap-3">
      <Segmented label="Show" options={FILTERS} value={filter} onChange={setFilter} />
      {shown.length === 0 ? (
        <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          {filter !== "all" && query.hasNextPage ? "None in the latest activity. Show older to look further back." : "Nothing yet."}
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {shown.map((e) => (
            <ActivityRow key={e.id} event={e} source={connector.source.kind} />
          ))}
        </ul>
      )}
      {query.hasNextPage && (
        <Button
          size="sm"
          variant="outline"
          className="w-fit self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? "Loading…" : "Show older"}
        </Button>
      )}
    </div>
  );
}

function ActivityRow({ event, source }: { event: ConnectorActivity; source: ConnectorSourceKind }) {
  const Icon = KIND_ICON[event.kind] ?? Play;
  const outcome = event.outcome ? callOutcome(event.outcome) : null;
  return (
    <li className="flex items-start gap-3 p-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          {event.kind === "call" && event.action ? (
            <>
              <code className="font-mono text-sm font-medium">{event.action}</code>
              {outcome && <span className={cn("ml-2 text-xs font-medium", TONE_CLASS[outcome.tone])}>{outcome.label}</span>}
              {event.durationMs != null && (
                <span className="ml-2 text-xs text-muted-foreground tabular-nums">{event.durationMs} ms</span>
              )}
            </>
          ) : (
            event.detail
          )}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {event.kind === "call" && event.detail ? `${event.detail} · ` : ""}
          {actorName(event.actor, source)} · {formatWhen(event.at)}
        </p>
      </div>
      {event.kind !== "call" && (
        <Badge variant="outline" className="shrink-0 font-normal text-muted-foreground">
          {KIND_LABEL[event.kind] ?? "Code"}
        </Badge>
      )}
    </li>
  );
}
